import { RootState } from "../index";
import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { blockUser, banIpUser, fetchUser, fetchUsers, updateUser } from "../../actions/mine_craft/usersActions";
import { fetchComment, fetchComments, fetchReplies } from "../../actions/mine_craft/commentsActions";
import { fetchReport, fetchReports } from "../../actions/mine_craft/reportsActions";
import { fetchAddon, fetchAddons } from "../../actions/mine_craft/addonsActions";
import { fetchAccounts } from "../../actions/mine_craft/accountsActions";
import { fetchBanner, fetchBanners } from "../../actions/mine_craft/bannersActions";
import { fetchAvatar, fetchAvatars } from "../../actions/mine_craft/avatarsActions";

export interface UserInterface {
  id: number;
  app_id: number;
  name: string;
  email: string;
  url: string;
  avatar_id?: number;
  avatar_url: string;
  about_me: string;
  gamertag: string | null;
  coins: number;
  addons_count: number;
  comments_count?: number;
  likes_count: number;
  downloads_count: number;
  following_counter: number;
  followers_counter: number;
  banned: boolean;
  banned_until: string;
  muted: boolean;
  muted_until: string;
  created_at: string;
  updated_at: string;
  current_sign_in_at: string;
  native_ads: boolean;
  banner: boolean;
  app_open: boolean;
  interstitial: boolean;
  interstitial_long: boolean;
  is_online: boolean,
  notify_youtube_video: boolean,
  notify_app_update: boolean,
  notify_follow_posts: boolean,
  notify_follow: boolean,
  notify_comment_account: boolean,
  notify_like_account: boolean,
  notify_like_post: boolean,
  notify_comment_post: boolean,
  notify_download_post: boolean,
  notify_like_comment: boolean,
  notify_comment_reply: boolean,
  notify_daily_rewards: boolean,
  step_follow_posts: number,
  step_follow: number,
  step_comment_account: number,
  step_like_account: number,
  step_like_post: number,
  step_comment_post: number,
  step_download_post: number,
  step_like_comment: number,
  step_comment_reply: number,
  bundle?: {
    id: EntityId;
    name: string;
  };
  addons?: number[];
  last_addons?: number[];
  bundle_ico_url: string;
  reg_sign_in_ip: string | null;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  banId: EntityId | null;
  banError: string | null;

  previewAddons: number[];
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  banId: null,
  banError: null,

  previewAddons: []
};

const usersAdapter = createEntityAdapter<UserInterface>();

const slice = createSlice({
  name: 'users',
  initialState: usersAdapter.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setBanId: (state, action) => {
      state.banId    = action.payload;
      state.banError = initialState.banError;
    },
    setBanError: (state, action) => {
      state.banError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchUsers.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.users;
        state.previewAddons = action.payload.previewAddons;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchUser.fulfilled, (state, action) => {
        const user = Object.values(action.payload.user)[0];
        state.entities[user.id] = user;
        state.previewAddons = [...state.previewAddons, ...user.last_addons];
      })

      // #update
      .addCase(updateUser.fulfilled, (state, action) => {
        const user = Object.values(action.payload.user)[0];
        state.entities[user.id] = user;
        state.previewAddons = [...state.previewAddons, ...user.last_addons];
      })
      .addCase(updateUser.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #ban
      .addCase(blockUser.fulfilled, (state, action) => {
        const user = Object.values(action.payload.user)[0];
        state.entities[user.id] = user;
        state.previewAddons = [...state.previewAddons, ...user.last_addons];
      })
      .addCase(blockUser.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.banError = error || action.error.message || 'Unknown Error';
      })

      // #ban_ip
      .addCase(banIpUser.fulfilled, (state, action) => {
        const user = Object.values(action.payload.user)[0];
        state.entities[user.id] = user;
        state.previewAddons = [...state.previewAddons, ...user.last_addons];
      })
      .addCase(banIpUser.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.banError = error || action.error.message || 'Unknown Error';
      })

      // #comments
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchReplies.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchComment.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user };
      })

      // #reports
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users, ...action.payload.active_users };
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user, ...action.payload.active_user };
      })

      // #addons
      .addCase(fetchAddons.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchAddon.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user };
      })

      // #banners
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchBanner.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user };
      })

      // #avatars
      .addCase(fetchAvatars.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchAvatar.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user };
      })

      // accounts
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  setBanId,
  setBanError
} = slice.actions;

export const {
  selectById: selectUserById,
  selectIds: selectUserIds,
  selectEntities: selectUserEntities
} = usersAdapter.getSelectors<RootState>(state => state.mineCraft.users);
